"use client";

import { createContext, useContext, useSyncExternalStore, type MutableRefObject } from "react";

export type SidePanelKind = "inspect" | "docked" | "task";
export type SidePanelSize = "S" | "M" | "L";

/** Fixed widths in px. Below 768px every kind is full screen instead. */
export const SIDE_PANEL_WIDTHS: Record<SidePanelSize, number> = { S: 400, M: 480, L: 640 };

export { SIDE_PANEL_FOCUS_RING } from "src/components/ui/side-panel/sidePanelStyles";

/**
 * Layers that handle their own keys: Radix popovers, selects, menus and dialogs, and other modal
 * layers. An Esc pressed in one is theirs, not a panel's, and focus in one is not in the page.
 */
export const SIDE_PANEL_PORTALLED_LAYER_SELECTOR =
  '[data-radix-popper-content-wrapper], [role="dialog"], [role="alertdialog"], [role="listbox"], [role="menu"]';

/** Elements that take focus from Tab. */
export const SIDE_PANEL_FOCUSABLE_SELECTOR =
  'button:not([disabled]), a[href], input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

/** What the header registers so the panel's keyboard handling can reach it. */
export interface SidePanelHeaderHandlers {
  onBack?: () => void;
  onPrevious?: () => void;
  onNext?: () => void;
  onPreviousField?: () => void;
  onNextField?: () => void;
}

/** What the footer registers so ⌘/Ctrl+Enter follows the visible primary button. */
export interface SidePanelFooterPrimaryState {
  isDisabled: boolean;
}

export interface SidePanelContextValue {
  kind: SidePanelKind;
  label: string;
  titleId: string;
  titleRef: MutableRefObject<HTMLHeadingElement | null>;
  headerHandlersRef: MutableRefObject<SidePanelHeaderHandlers | null>;
  /** The footer reports its primary button here (null when it has none). */
  registerFooterPrimary: (state: SidePanelFooterPrimaryState | null) => void;
  isFullScreen: boolean;
  /** Close, unless a dirty task panel must ask first (or the panel cannot close right now). */
  requestClose: () => void;
  /** The close button and phone back link show as disabled (a task saving its work). */
  isCloseDisabled: boolean;
  /** Present only on a collapsible docked panel. */
  collapse?: () => void;
  /** Link text that closes the panel at phone width ("Scope", "Cancel"). */
  phoneBackLabel: string;
}

export const SidePanelContext = createContext<SidePanelContextValue | null>(null);

export function useSidePanelContext(component: string): SidePanelContextValue {
  const context = useContext(SidePanelContext);
  if (!context) {
    throw new Error(`${component} must be used within a SidePanel`);
  }
  return context;
}

const PHONE_QUERY = "(max-width: 767px)";

function subscribeMediaQuery(query: string, onChange: () => void): () => void {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") {
    return () => undefined;
  }
  const mediaQuery = window.matchMedia(query);
  if (typeof mediaQuery.addEventListener === "function") {
    mediaQuery.addEventListener("change", onChange);
    return () => mediaQuery.removeEventListener("change", onChange);
  }
  mediaQuery.addListener(onChange);
  return () => mediaQuery.removeListener(onChange);
}

function matchesMediaQuery(query: string): boolean {
  if (typeof window === "undefined" || typeof window.matchMedia !== "function") return false;
  return window.matchMedia(query).matches;
}

const subscribePhoneWidth = (onChange: () => void) => subscribeMediaQuery(PHONE_QUERY, onChange);
const getPhoneWidthSnapshot = () => matchesMediaQuery(PHONE_QUERY);

/** True below 768px, where every panel kind renders full screen. SSR renders the wide layout. */
export function useIsPhoneWidth(): boolean {
  return useSyncExternalStore(subscribePhoneWidth, getPhoneWidthSnapshot, () => false);
}

/**
 * How an outlet places its panels:
 * - push: an in-flow column beside the page, so the page shrinks and stays visible;
 * - overlay: over the right edge of the page, under the navbar, when pushing would leave the page
 *   less than `SIDE_PANEL_MIN_PAGE_WIDTH`;
 * - full-screen (<768px): every kind takes the whole screen.
 */
export type SidePanelLayout = "push" | "overlay" | "full-screen";

/**
 * The layout the viewport allows: full screen below 768px, otherwise push. The outlet turns push
 * into overlay when the open panels leave the page too little room, which only it can measure.
 * SSR renders push, like `useIsPhoneWidth`.
 */
export function useSidePanelLayout(): SidePanelLayout {
  return useIsPhoneWidth() ? "full-screen" : "push";
}
